import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { apiGet, apiPost } from "util/api";
import startCase from "lodash/startCase";
import uniq from "lodash/uniq";
import moment from "moment";
import * as actions from "../actions";
import workareaViewStateToProps from "../selectors";
import ElabBulkLayout from "./ElabBulkLayout";

const createEmptyObjectsArray = num => Array.from(Array(num)).map(() => ({}));
const initialNumberOfRows = 1000;
const getEmptyTable = () => createEmptyObjectsArray(initialNumberOfRows);

const elabSampleProperties = [
  // "sampleTypeID", // num
  "name",
  // "description",
  // "parentSampleID",
  "note", // optional
  "position", // num
  "storageLayerID" // num,
];

class ElabBulkView extends React.Component {
  // the reference to the AggridAPI is too big to store in redux
  state = { storageLocation: null };

  componentDidMount() {
    document.title = "eLAB Bulk Uploader";
  }

  // translate storage layer names to storage layer IDs
  updateRow = (rowNode, field, value) => {
    // blank it first so it flashes
    rowNode.setDataValue(field, "");
    rowNode.setDataValue(field, value);
    rowNode.gridApi.refreshCells();
    rowNode.gridApi.sizeColumnsToFit();
  };

  // get the column and row start/end of the current selection (drag to select)
  // start will always be less or equal to end
  // eslint-disable-next-line consistent-return
  getSelectionRange = () => {
    const { gridApi } = this;
    const { columnDefs } = this.props;
    const columnDefNames = columnDefs.map(colDef => colDef.field);
    const range = gridApi.getRangeSelections();
    if (range && range.length) {
      const selection = gridApi.getRangeSelections()[0];
      const rowA = selection.start.rowIndex;
      const rowB = selection.end.rowIndex;
      const colA = columnDefNames.indexOf(selection.start.column.colId);
      const colB = columnDefNames.indexOf(selection.end.column.colId);
      // this way whether selecting left to right, up to down,down to up give
      // same selection range

      return {
        startRow: Math.min(rowA, rowB),
        endRow: Math.max(rowA, rowB),
        startCol: Math.min(colA, colB),
        endCol: Math.max(colA, colB)
      };
    }
  };

  // aggrid column definitions
  setupGridColumns = sampleType => {
    const { sampleTypes } = this.props;

    // let sampleTypeMetaDefinitions = [];
    // if (sampleType && sampleTypes) {

    // get definiteion for current sample type
    const sampleTypeMetaDefinitions = sampleTypes.find(
      s => s.sampleTypeID === sampleType
    ).meta;

    // }
    // only these less complicated types will show up as editable meta fields
    const editableMetaTypes = ["DATE", "TEXT", "COMBO", "NUMERIC"];

    // combine default list of editable fields (name,note etc) with type-specific fields
    const fields = elabSampleProperties.concat(
      sampleTypeMetaDefinitions
        .filter(s => editableMetaTypes.includes(s.sampleDataType))
        .map(s => s.key)
    );

    const responseColDefinition = {
      headerName: "Response",
      field: "response",
      editable: false,
      cellStyle: data => {
        if (data.value === "fulfilled")
          return {
            color: "black"
          };
        return {
          color: "red"
        };
      }
    };
    // these built-in fields are required
    const requiredFieldsDefault = ["name", "storageLayerID"];

    const requiredMetaFields = sampleTypeMetaDefinitions
      .filter(m => m.required && editableMetaTypes.includes(m.sampleDataType))
      .map(e => e.key);
    const requiredFields = [...requiredFieldsDefault, ...requiredMetaFields];

    const columnDefs = fields
      .map(field => {
        const metaFieldDefinition = sampleTypeMetaDefinitions.find(
          s => s.key === field
        );
        const fieldIsDropDown =
          metaFieldDefinition && metaFieldDefinition.sampleDataType === "COMBO";
        return {
          headerName: startCase(field),
          field,
          // if its a meta field and we have data type then use that

          cellEditor: fieldIsDropDown ? "agRichSelectCellEditor" : null,
          cellEditorParams: fieldIsDropDown
            ? { values: metaFieldDefinition.optionValues }
            : null,
          suppressCellFlash: true,
          editable: ({ data }) => {
            return data.response !== "fulfilled";
          }
        };
      })
      .concat(responseColDefinition);

    const getColDefByField = field => columnDefs.find(c => c.field === field);

    const nameColDef = getColDefByField("name");
    nameColDef.headerName = "Name";
    nameColDef.width = 75;
    getColDefByField("storageLayerID").headerName = "Storage Location";

    requiredFields.forEach(field => {
      const colDef = getColDefByField(field);
      if (colDef) colDef.headerName += "<font color='red'>*</font>";
      else console.error(`Cant find ${field}`);
    });
    const positionColDef = getColDefByField("position");
    positionColDef.width = 50;
    positionColDef.cellEditor = "numericEditor";

    const { storageLocations } = this.props;
    const hasDuplicates = arr => uniq(arr).length !== arr.length;

    if (hasDuplicates(storageLocations.map(s => s.label)))
      throw new Error("Duplicate storage location names exist!");

    this.props.actions.setColumnDefs(columnDefs);
  };

  // get a token
  elabLogin = async () => {
    const { username, password } = this.props;

    this.props.actions.toggle("isFetchingLogin");

    return apiPost("/elab/auth", {
      username,
      password
    })
      .then(res => res.json())
      .then(({ token: elabToken, user }) => {
        this.props.actions.setElabUser({
          elabToken,
          user: user.username
        });
      })
      .catch(() => {
        this.props.actions.setElabUserFailed();
      });
  };

  //  get array of sample type objects like  [{label:'my sample type',value:123}]
  elabGetSampleTypes = async () => {
    const { elabToken } = this.props;
    this.props.actions.toggle("isFetchingSampleTypes");
    const sampleTypes = await apiGet("/elab/sample_types", {
      headers: {
        elabToken
      }
    }).then(res => res.json());
    this.props.actions.setSampleTypes(
      sampleTypes.filter(s => s.name !== "Chemical")
    );
  };

  // upload samples from the grid
  elabPostSamples = async samples => {
    // then color some rows green and some rows red
    const { elabToken, sampleType, log, sampleTypeObj } = this.props;
    const { gridApi } = this;
    const { meta } = sampleTypeObj;
    const samplesWithSampleType = samples.map(s => {
      const { rowNode, ...data } = s;
      return {
        ...data,
        sampleTypeID: sampleType
      };
    });
    // meta fields for this sample type
    // const metaFields = sampleTypeObj.meta.map(m => m.key);

    // need to send COMBO fields as {fieldID}
    const samplesWithMetaMoved = samplesWithSampleType.map(s => {
      // gather meta fields
      const meta2 = meta
        .map(f => {
          let sampleTypeMetaDefaultsID;
          const value = s[f.key];
          if (f.sampleDataType === "COMBO" && value) {
            // lookup the combo box value in the definitions and get the ID for that value

            const index = f.optionValues.indexOf(value);

            if (index >= 0)
              sampleTypeMetaDefaultsID = f.sampleTypeMetaDefaultsIDs[index];
            else throw new Error(`Invalid value: ${value}`);
          }
          // this JSON object just gets passed through the API to eLAB
          return {
            key: f.key,
            // seems like if its a dropdown it still neds a value set
            value,
            sampleDataType: f.sampleDataType,
            // both 'sampleTypeMetaID' and 'key' seem to be required for this to work properly, seems redundant
            sampleTypeMetaID: f.sampleTypeMetaID,
            // this field only relevant to drop downs
            sampleTypeMetaDefaultsIDs: sampleTypeMetaDefaultsID && [
              sampleTypeMetaDefaultsID
            ]
          };
        })
        .filter(m => m.value);

      // clean non-meta fields
      Object.keys(s).forEach(field => {
        // eslint-disable-next-line no-param-reassign
        if (meta.map(m => m.key).includes(field)) delete s[field];
      });
      return { ...s, meta: meta2 };
    });

    this.props.actions.toggle("isPostingSamples");
    gridApi.showLoadingOverlay();
    const response = await apiPost("/elab/samples", samplesWithMetaMoved, {
      headers: {
        elabToken
      }
    }).then(res => res.json());
    gridApi.hideOverlay();

    response.forEach((res, i) => {
      const message =
        (res.data && res.data.message) || res.statusText || res.status;
      const { rowNode } = samples[i];

      // rowNode.data.sampleID = res.pk;
      rowNode.setDataValue("response", "");
      rowNode.setDataValue("response", message);
    });
    gridApi.refreshCells();

    this.props.actions.toggle("isPostingSamples");

    // fill out log, might ditch this
    const numFulfilled =
      response.filter(res => res.status === "fulfilled").length || 0;

    const numFailed = response.length - numFulfilled;

    log.push({
      date: moment().format(`YYYY-MM-DD hh:mm:ss a	`),
      total: response.length,
      failed: numFailed,
      fulfilled: numFulfilled
    });
  };

  // get and filter storage locations to array like [{label:"shelf 1",value:123}]
  elabGetStorageLocations = async () => {
    const { elabToken } = this.props;
    this.props.actions.toggle("isFetchingStorageLocations");

    const storageLocations = await apiGet("/elab/storage_locations", {
      headers: {
        elabToken
      }
    }).then(res => res.json());

    this.props.actions.setStorageLocations(storageLocations);
  };

  // storageID,name
  async loginButtonClick() {
    this.elabLogin().then(() => {
      if (this.props.user) {
        this.elabGetSampleTypes();
        this.elabGetStorageLocations();
      }
    });
  }

  // set grid to blankrows
  clearTable() {
    const { gridApi } = this;
    if (gridApi) gridApi.setRowData(getEmptyTable());
  }

  render() {
    return (
      <ElabBulkLayout
        loginButtonClick={() => this.loginButtonClick()}
        logoutButtonClick={() => this.props.actions.setElabUser({})}
        onSampleTypeChange={v => {
          const { sampleType } = this.props;
          const newSampleType = v.value;
          if (newSampleType !== sampleType) {
            // this.clearTable();
            this.props.actions.setSampleType(newSampleType);
            // setup new column defs
            this.setupGridColumns(newSampleType);
          }
        }}
        setGridAPI={gridApi => {
          this.gridApi = gridApi;
          this.clearTable();
        }}
        onStorageLocationChange={v => {
          this.setState({ storageLocation: v.value });
        }}
        addRows={(count = 1) => {
          const { gridApi } = this;

          gridApi.updateRowData({
            add: createEmptyObjectsArray(count)
          });
        }}
        clearData={() => {
          // if no cell is selected to be cleared then do nothing
          if (!this.getSelectionRange()) return;

          const { columnDefs } = this.props;

          const { gridApi } = this;
          const columnDefNames = columnDefs.map(colDef => colDef.field);

          const {
            startRow,
            endRow,
            startCol,
            endCol
          } = this.getSelectionRange();

          const colsToClear = columnDefNames.slice(startCol, endCol + 1);

          const rowNodesToClear = [];
          gridApi.forEachNode((node, i) => {
            if (i >= startRow && i <= endRow) rowNodesToClear.push(node);
          });

          rowNodesToClear.forEach(r => {
            colsToClear.forEach(col => {
              r.setDataValue(col, "");
            });
          });
        }}
        setUsername={v => {
          this.props.actions.setUsername(v);
        }}
        refreshELabData={() => {
          const { sampleType } = this.props;
          this.elabGetStorageLocations();
          this.elabGetSampleTypes();

          if (sampleType) this.setupGridColumns(sampleType);
        }}
        setCellToStorageLocation={() => {
          const { storageLocations } = this.props;
          const selectionRange = this.getSelectionRange();

          // if no cell is selected
          if (!selectionRange) return;

          const { startRow, endRow } = selectionRange;
          const { gridApi } = this;
          const { storageLocation } = this.state;

          gridApi.forEachNode((node, i) => {
            if (i >= startRow && i <= endRow) {
              node.setDataValue(
                "storageLayerID",
                storageLocations.find(s => s.value === storageLocation).label
              );
            }
          });
        }}
        setPassword={v => {
          this.props.actions.setPassword(v);
        }}
        gridApi={this.gridApi}
        storageLocation={this.state.storageLocation}
        // this is where most of the validation happens
        uploadData={() => {
          const { storageLocations, sampleType, sampleTypes } = this.props;
          const { gridApi } = this;

          if (!gridApi) return;

          const sampleTypeMetaDefinitions = sampleTypes.find(
            s => s.sampleTypeID === sampleType
          ).meta;

          const rowNodes = [];

          gridApi.forEachNode(node => rowNodes.push(node));

          const rowHasData = obj =>
            Object.keys(obj).filter(k => obj[k]).length > 0;

          const getStorageLocationIDByName = label => {
            const storageLocation = storageLocations.find(
              s => s.label === label
            );
            if (storageLocation) return storageLocation.value;
            return false;
          };
          const sampleIDList = [];
          // client side validation
          const rowDataWithStorageIDs = rowNodes
            .map(rowNode => {
              const { data } = rowNode;
              const dataToUpload = { ...data };

              const { name, storageLayerID, position } = data;

              // empty row
              if (!rowHasData(data)) return false;

              // theres already a sampleID set, so it's been uploaded already
              if (data.response === "fulfilled") return false;

              if (!name) {
                this.updateRow(
                  rowNode,
                  "response",
                  "Missing name or storage location"
                );
                return false;
              }

              if (sampleIDList.includes(data.name)) {
                this.updateRow(rowNode, "response", "Duplicate name");
                return false;
              }
              // eslint-disable-next-line no-restricted-globals
              if (position && isNaN(Number.parseInt(position, 10))) {
                this.updateRow(rowNode, "response", "position must be number");
                return false;
              }

              sampleIDList.push(name);

              dataToUpload.altID = data.name;

              // will return false if it cant find this location
              const storageLocationID = getStorageLocationIDByName(
                storageLayerID
              );

              if (storageLocationID)
                dataToUpload.storageLayerID = storageLocationID;
              else {
                this.updateRow(rowNode, "response", "invalid storage location");
                return false;
              }

              // iterate through the meta fields to validate them
              const metaErrs = sampleTypeMetaDefinitions
                .map(field => {
                  const { optionValues, sampleDataType, key, required } = field;

                  // get the grid data for this meta field
                  const metaData = dataToUpload[key];

                  // this user-defined field is required
                  if (required && !metaData)
                    return `${key} is a required field`;

                  // eslint-disable-next-line no-restricted-globals
                  if (sampleDataType === "NUMERIC" && isNaN(metaData)) {
                    return `${key} must be number`;
                  }
                  const dateRegex = /\d{4}-\d{2}-\d{2}/;
                  // eslint-disable-next-line no-restricted-globals
                  if (metaData && sampleDataType === "DATE") {
                    const date = moment(metaData);

                    if (!metaData.match(dateRegex) || !date.isValid())
                      return `${key} must be a date YYYY-MM-DD`;
                  }
                  if (
                    metaData &&
                    sampleDataType === "COMBO" &&
                    !optionValues.includes(metaData)
                  ) {
                    return `${key} does not include option:${metaData}`;
                  }
                  return false;
                })
                .filter(e => e);

              if (metaErrs.length) {
                this.updateRow(rowNode, "response", metaErrs[0]);
                return false;
              }

              // we keep this along with the row to make it easier to update this row
              // not ideal, but this is how the Aggrid API works
              dataToUpload.rowNode = rowNode;

              // dont send them the response
              delete dataToUpload.response;

              return dataToUpload;
            })
            .filter(e => e);

          if (rowDataWithStorageIDs.length)
            this.elabPostSamples(rowDataWithStorageIDs);
        }}
        {...this.props}
      />
    );
  }
}

const sampleTypeObjShape = PropTypes.shape({
  backgroundColor: PropTypes.string,
  defaultQuantity: PropTypes.number,
  defaultQuantityType: PropTypes.string,
  defaultThresholdAction: PropTypes.string,
  defaultUnit: PropTypes.string,
  defaultUnitShort: PropTypes.string,
  deleted: PropTypes.bool,
  description: PropTypes.string,
  foregroundColor: PropTypes.string,
  meta: PropTypes.shape({}),
  groupID: PropTypes.number,
  name: PropTypes.string,
  quantityRequired: PropTypes.bool,
  sampleTypeID: PropTypes.number,
  thresholdEnables: PropTypes.bool,
  unserID: PropTypes.number
});

ElabBulkView.propTypes = {
  user: PropTypes.string,
  elabToken: PropTypes.string,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  sampleType: PropTypes.string,
  sampleTypes: PropTypes.arrayOf(PropTypes.shape(sampleTypeObjShape)),
  sampleTypeObj: PropTypes.shape(sampleTypeObjShape),
  // eslint-disable-next-line react/forbid-prop-types
  log: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  getRowStyle: PropTypes.func,
  storageLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  isFetchingLogin: PropTypes.bool.isRequired,
  isFetchingSampleTypes: PropTypes.bool.isRequired,
  isFetchingStorageLocations: PropTypes.bool.isRequired,
  columnDefs: PropTypes.arrayOf(PropTypes.object).isRequired,
  elabUserFailed: PropTypes.bool.isRequired,

  actions: PropTypes.shape({
    setElabUser: PropTypes.func.isRequired,
    setElabUserFailed: PropTypes.func.isRequired,
    // setGridAPI: PropTypes.func.isRequired,
    setStorageLocations: PropTypes.func.isRequired,
    setSampleTypes: PropTypes.func.isRequired,
    setSampleType: PropTypes.func.isRequired,
    setColumnDefs: PropTypes.func.isRequired,
    setUsername: PropTypes.func.isRequired,
    setPassword: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired
  })
}.isRequired;

ElabBulkView.defaultProps = {
  getRowStyle: () => ({}),
  sampleTypeObj: {},
  sampleType: "",
  sampleTypes: [],
  elabToken: "",
  user: ""
};
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

export default withRouter(
  connect(workareaViewStateToProps, mapDispatchToProps)(ElabBulkView)
);
